import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';
import { verifyTOTP } from '@/lib/totp';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Shield, AlertCircle } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function Verify2Fa() {
  const { user, userProfile, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login', { replace: true });
      } else if (userProfile && !userProfile.mfaEnabled) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, userProfile, authLoading, navigate]);

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db || !user) return;
    setLoading(true);
    try {
      const totpDoc = await getDoc(doc(db, 'users', user.uid, 'mfa_config', 'totp'));
      if (totpDoc.exists()) {
        const { secret } = totpDoc.data();
        const isValid = await verifyTOTP(secret, totpCode);
        if (isValid) {
          // Set session verified
          sessionStorage.setItem(`user_2fa_verified_${user.uid}`, 'true');
          setTotpCode('');
          toast({ title: 'Access Granted', description: 'Two-factor verification successful.' });
          navigate(from, { replace: true });
        } else {
          setTotpError('Invalid verification code. Please check your authenticator and try again.');
        }
      } else {
        // Fallback: If they don't have TOTP setup in database but got here, bypass
        sessionStorage.setItem(`user_2fa_verified_${user.uid}`, 'true');
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      console.error('Error verifying MFA code:', error);
      setTotpError('An error occurred during verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-emerald-50 px-4 py-12">
      <SEO title="Two-Factor Authentication - QuickFreeInvoice" description="Verification gate page." noindex={true} />
      <Card className="w-full max-w-md shadow-2xl border border-gray-150">
        <CardHeader className="text-center">
          <div className="mx-auto bg-primary/10 p-3 rounded-full w-fit mb-2">
            <Shield className="h-8 w-8 text-primary animate-pulse" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Two-Factor Authentication</CardTitle>
          <CardDescription>
            This account is protected by 2FA. Enter the 6-digit verification code from your authenticator app.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfaCode" className="text-sm font-medium">Verification Code</Label>
              <Input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                className="text-center text-2xl font-mono tracking-widest"
                value={totpCode}
                onChange={(e) => {
                  setTotpCode(e.target.value.replace(/\D/g, ''));
                  setTotpError(null);
                }}
                autoFocus
                required
              />
              {totpError && (
                <p className="text-sm text-destructive flex items-center gap-1.5 mt-1">
                  <AlertCircle className="h-4 w-4" />
                  {totpError}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full text-sm text-muted-foreground hover:text-gray-900"
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
